/**
 * Контакты (изменение v1.6 — явный список, как в Telegram).
 *
 * Контакт — человек, которого пользователь добавил сам: из профиля, из результата поиска по
 * никнейму или по номеру телефона. Связь односторонняя и живёт на сервере (`GET /contacts`,
 * события `contact.created` / `contact.deleted`); взаимное «Го» добавляет пару друг другу
 * автоматически — это уже согласие обеих сторон.
 *
 * Прямой диалог контактом НЕ делает: человек, который написал по никнейму, остаётся в «Чатах»,
 * пока его не добавят. Поэтому строка списка дополняется диалогом и совпадением из реплики —
 * они дают presence, `conversation_id` и признак «есть совпадение».
 */
import type {
  Availability,
  Contact as ApiContact,
  Match,
  ModeName,
  Presence,
  PublicPhoto,
} from '@shared/api/types'
import { contactsRepo, conversationsRepo, matchesRepo, useReplicaList } from '@shared/db'

export interface Contact {
  /** id пользователя. */
  id: string
  name: string | null
  username: string | null
  photo: PublicPhoto | null
  availability: Availability
  presence: Presence | null
  /** Диалог пары, если он уже начат. */
  conversationId: string | null
  /** Агрегат совпадения пары; null — контакт без совпадения. */
  match: Match | null
  /** Есть активный прямой диалог (не по совпадению). */
  direct: boolean
  modes: ModeName[]
  /** Когда контакт добавлен (ISO). */
  since: string
}

/** Недоступный профиль показывается нейтральной подписью (ПС §10, §19). */
export function contactDisplayName(contact: Pick<Contact, 'name' | 'availability'>): string {
  if (contact.availability === 'deleted') return 'Удалённый аккаунт'
  if (contact.availability !== 'available') return 'Профиль недоступен'
  return contact.name ?? 'Без имени'
}

function fromRecord(record: ApiContact): Contact {
  const card = record.user
  return {
    id: record.user_id,
    name: card.display_name,
    username: card.username,
    photo: card.photos[0] ?? null,
    availability: card.availability,
    presence: card.presence ?? null,
    conversationId: null,
    match: null,
    direct: false,
    modes: card.modes_in_common ?? [],
    since: record.added_at,
  }
}

async function loadContacts(): Promise<Contact[]> {
  const [records, matches, conversations] = await Promise.all([
    contactsRepo.all(),
    matchesRepo.listActive(),
    conversationsRepo.list('all'),
  ])

  const items = new Map<string, Contact>()
  for (const record of records) items.set(record.user_id, fromRecord(record))

  // Совпадение даёт диалог, режимы и свежий presence.
  for (const match of matches) {
    const contact = items.get(match.counterpart.id)
    if (!contact) continue
    contact.match = match
    contact.conversationId = match.conversation_id
    contact.modes = match.modes
    if (match.counterpart.presence !== undefined) {
      contact.presence = match.counterpart.presence ?? contact.presence
    }
  }

  // Диалог — самый свежий источник имени, никнейма и присутствия.
  for (const conversation of conversations) {
    // Лента канала в списке контактов не участвует: у неё нет контрагента (изменение v1.7);
    // секретный чат (v1.13) — не «диалог пары» и на карточку контакта не влияет.
    if (conversation.state !== 'active' || !conversation.counterpart || conversation.kind === 'secret')
      continue
    const contact = items.get(conversation.counterpart.id)
    if (!contact) continue
    const person = conversation.counterpart
    contact.direct = contact.direct || conversation.direct
    if (!contact.conversationId) contact.conversationId = conversation.id
    if (person.presence !== undefined) contact.presence = person.presence ?? contact.presence
    if (person.username) contact.username = person.username
    if (person.display_name) contact.name = person.display_name
  }

  return [...items.values()]
}

/** Все контакты из реплики; порядок — на усмотрение экрана (имя или активность). */
export function useContacts(): { items: Contact[]; loaded: boolean } {
  return useReplicaList(loadContacts, [])
}
